import { useEffect } from "react";
import ReactDOM from "react-dom";
import { useForm } from "react-hook-form";
import { FiX } from "react-icons/fi";
import api from "../../configs/api";
import { useScrollLock } from "../../hooks/useScrollLock";
import { Z_INDEX } from "../../utils/constants";
import CompanyFormFields from "../admin/profile-subscription/CompanyFormFields";

const CompanyModal = ({
  open,
  mode = "create",
  initialData = null,
  onClose,
  onSuccess,
}) => {
  useScrollLock(open);
  // console.log("initialData:", initialData);
  const isEdit = mode === "edit";
  const defaultForm = {
    companyLogo: initialData?.companyLogo || "",
    companyName: initialData?.companyName || "",
    foundedYear: initialData?.foundedYear || "",
    companyReraId: initialData?.reraId || "",
    companyDescription: initialData?.description || "",
    totalProjects: initialData?.totalProjects || "",
    experienceYears: initialData?.experienceYears || "",
    citiesCovered: initialData?.citiesCovered || "",
    newLaunch: initialData?.newLaunch || "",
    readyToMove: initialData?.readyToMove || "",
    underConstruction: initialData?.underConstruction || "",
  };
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    defaultValues: defaultForm,
  });

  useEffect(() => {
    if (initialData) {
      reset(defaultForm);
    } else {
      reset({
        companyLogo: "",
        companyName: "",
        foundedYear: "",
        companyReraId: "",
        companyDescription: "",
        totalProjects: "",
        experienceYears: "",
        citiesCovered: "",
        newLaunch: "",
        readyToMove: "",
        underConstruction: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, reset]);

  if (!open) return null;

  const handleSafeClose = () => {
    if (isDirty && !window.confirm("You have unsaved changes. Close anyway?")) {
      return;
    }
    onClose?.();
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        companyName: values.companyName,
        companyLogo: values.companyLogo,
        foundedYear: values.foundedYear
          ? Number(values.foundedYear)
          : undefined,
        reraId: values.companyReraId,
        description: values.companyDescription,
        totalProjects: values.totalProjects
          ? Number(values.totalProjects)
          : undefined,
        experienceYears: values.experienceYears
          ? Number(values.experienceYears)
          : undefined,
        citiesCovered: values.citiesCovered
          ? Number(values.citiesCovered)
          : undefined,
        newLaunch: values.newLaunch ? Number(values.newLaunch) : undefined,
        readyToMove: values.readyToMove
          ? Number(values.readyToMove)
          : undefined,
        underConstruction: values.underConstruction
          ? Number(values.underConstruction)
          : undefined,
      };

      if (isEdit) {
        const updated = await api.put(`/companies/${initialData._id}`, payload);
        // console.log("updated:", updated?.data?.updated);
        onSuccess?.(updated.data?.updated);
      } else {
        const created = await api.post(`/companies`, payload);
        // console.log("created:", created?.data?.company);
        onSuccess?.(created.data?.company);
      }

      onSuccess?.(payload);
      onClose?.();
    } catch (error) {
      console.error("Failed to save company:", error);
      alert("Failed to save company. Please try again.");
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{ zIndex: Z_INDEX.modal }}
      className="fixed inset-0  flex items-center justify-center bg-black/30 "
    >
      <div
        className="
        w-full 
        max-w-2xl 
        max-h-[80vh]
        sm:max-h-[88vh] 
        mx-4 
        bg-white 
        rounded-2xl 
        shadow-2xl 
        overflow-hidden 
        animate-[fadeIn_0.2s_ease-out]
      "
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-2.5 sm:py-5 border-b border-gray-200 bg-gray-50/60">
          <h3 className="text-lg sm:text-xl font-semibold text-text-primary tracking-tight">
            {isEdit ? "Selected Company Details" : "Create New Company"}
          </h3>

          <button
            type="button"
            onClick={handleSafeClose}
            className="p-2 rounded-full hover:bg-gray-200/60 transition"
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form Scroll Container */}
        <div className="overflow-y-auto max-h-[calc(88vh-110px)] px-6 pt-5 pb-8">
          <form
            onSubmit={(e) => {
              e.stopPropagation();
              handleSubmit(onSubmit)(e);
            }}
            className="space-y-6"
          >
            <CompanyFormFields
              isEditing={true}
              register={register}
              errors={errors}
              data={initialData}
              setValue={setValue}
            />

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={handleSafeClose}
                className="px-5 py-2.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className="
                px-5 py-2.5 
                text-sm 
                font-semibold 
                rounded-lg 
                bg-primary text-white 
                hover:bg-primary/90 
                disabled:opacity-50 
                transition
                shadow-sm
              "
              >
                {isSubmitting
                  ? "Saving..."
                  : isEdit
                  ? "Save Changes"
                  : "Save and Add"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CompanyModal;
